import { dictionaryFor } from '@/i18n/dictionary'
import { LOCALES, DEFAULT_LOCALE, localeDef } from '@/i18n/locales'
import { localePath, routeIn, type RouteDef } from '@/app/routes'
import type { OgCard } from '@/lib/card'

/**
 * Everything that belongs in a page's `<head>`, as data.
 *
 * One function, two consumers: `scripts/prerender.mjs` serialises it into the
 * static HTML (which is what crawlers read), and `<Seo>` applies it to the live
 * document on client-side navigation (which is what a visitor gets after the
 * first page). They must not drift — a title that is right on load and wrong
 * after a nav click is the sort of bug nobody reports and everybody notices.
 */
export interface HeadData {
  title: string
  description: string
  canonical: string
  robots: string
  /** Serialised `<link>`/`<meta>` elements, in document order. */
  tags: { tag: 'link' | 'meta'; attrs: Record<string, string> }[]
}

export function headFor(opts: {
  route: RouteDef
  locale: string
  origin: string
  indexable: boolean
  /**
   * The social card, when the build has confirmed the file is really there.
   *
   * Passed in rather than imported because only the Node side can look at the
   * filesystem, and a tag naming a missing image is worse than no tag. The
   * browser never passes one, which costs nothing: no scraper runs JavaScript,
   * so the prerendered HTML is the only copy of these tags that is ever read.
   */
  card?: OgCard
}): HeadData {
  const { route, locale, origin, indexable, card } = opts
  const dict = dictionaryFor(locale)
  const page = dict.pages[route.id]

  // The titleless page (Home) used to join its two halves with an em dash,
  // which read as a stray line in the browser tab. It now uses the same
  // middot every other page's title already used.
  const title = page.title
    ? `${page.title} · ${dict.site.titleSuffix}`
    : `${dict.site.titleSuffix} · ${dict.site.tagline}`
  const description = page.description || dict.site.description
  const canonical = `${origin}${localePath(locale, route.path)}`
  // The route may opt OUT on an otherwise indexable site; it can never opt in
  // on a preview. Subtractive in one direction only — see `RouteDef.robots`.
  const robots =
    indexable && route.robots !== 'noindex' ? 'index, follow' : 'noindex, nofollow'

  const tags: HeadData['tags'] = [
    { tag: 'meta', attrs: { name: 'description', content: description } },
    { tag: 'meta', attrs: { name: 'robots', content: robots } },
    { tag: 'link', attrs: { rel: 'canonical', href: canonical } },
  ]

  // hreflang: every locale points at every other, including itself, and
  // `x-default` names the one an unmatched language should land on. Search
  // engines require the set to be reciprocal and absolute — a relative href or
  // a one-way reference is silently ignored, which is why these are generated
  // from the locale registry rather than written per page.
  //
  // Only the locales the page exists in (`RouteDef.locales`). A page written in
  // one language gets no set at all — the imported pages' rule: an alternate
  // that is only itself says nothing, and one naming a missing page is a lie.
  const siblings = LOCALES.filter((l) => routeIn(route, l.code))
  if (siblings.length > 1) {
    for (const l of siblings) {
      tags.push({
        tag: 'link',
        attrs: {
          rel: 'alternate',
          hreflang: l.code,
          href: `${origin}${localePath(l.code, route.path)}`,
        },
      })
    }
    tags.push({
      tag: 'link',
      attrs: {
        rel: 'alternate',
        hreflang: 'x-default',
        href: `${origin}${localePath(DEFAULT_LOCALE, route.path)}`,
      },
    })
  }

  tags.push(
    { tag: 'meta', attrs: { property: 'og:type', content: 'website' } },
    { tag: 'meta', attrs: { property: 'og:site_name', content: dict.site.name } },
    { tag: 'meta', attrs: { property: 'og:title', content: title } },
    { tag: 'meta', attrs: { property: 'og:description', content: description } },
    { tag: 'meta', attrs: { property: 'og:url', content: canonical } },
    { tag: 'meta', attrs: { property: 'og:locale', content: localeDef(locale).og } },
    { tag: 'meta', attrs: { name: 'twitter:card', content: 'summary_large_image' } },
    { tag: 'meta', attrs: { name: 'twitter:title', content: title } },
    { tag: 'meta', attrs: { name: 'twitter:description', content: description } },
  )

  // `og:locale:alternate`, one per OTHER live locale — the Open Graph half of
  // the `hreflang` set written above, and the piece that was missing while
  // `og:locale` was being published in the wrong form entirely (see the field's
  // note in `i18n/locales.ts`).
  //
  // WHY IT IS NOT THE SAME LOOP AS `hreflang`. The two answer different
  // questions and the difference is not cosmetic: `hreflang` is reciprocal and
  // INCLUDES the page's own locale — search engines ignore a set that leaves
  // itself out — while `og:locale:alternate` lists only the others, because
  // `og:locale` has already named this one and a page that declares itself an
  // alternate of itself is what Facebook's debugger flags.
  //
  // `LOCALES` and not `ALL_LOCALES`, so a paused locale is not advertised to a
  // scraper as somewhere it could send a reader — those four are redirected
  // away by the Worker and would unfurl as a bounce.
  for (const l of siblings) {
    if (l.code === locale) continue
    tags.push({ tag: 'meta', attrs: { property: 'og:locale:alternate', content: l.og } })
  }

  // THE CARD IMAGE, 2026-09-08. `summary_large_image` above has been here since
  // launch, promising a large card and naming no image — so every share of these
  // pages rendered as a bare link. Fatih reported it against the home page; it
  // was true of all 236.
  //
  // Absolute URLs: every scraper drops a root-relative path silently. Width and
  // height are declared so the card is laid out before the image is fetched.
  //
  // ONE CARD FOR EVERY PAGE, not just the home page. A home-only card fixes the
  // share that gets audited and leaves the ones that get sent — a tool link, a
  // report, a job opening — as bare links, which is the case that costs us.
  if (card) {
    tags.push(
      { tag: 'meta', attrs: { property: 'og:image', content: `${origin}${card.path}` } },
      { tag: 'meta', attrs: { property: 'og:image:type', content: card.mime } },
      { tag: 'meta', attrs: { property: 'og:image:width', content: String(card.width) } },
      { tag: 'meta', attrs: { property: 'og:image:height', content: String(card.height) } },
      { tag: 'meta', attrs: { property: 'og:image:alt', content: card.alt } },
      // Twitter reads `og:image` when this is absent, so it is belt-and-braces —
      // but X is the one platform that has changed which set it prefers, and a
      // duplicated 60-byte tag is cheaper than finding out it changed again.
      { tag: 'meta', attrs: { name: 'twitter:image', content: `${origin}${card.path}` } },
      { tag: 'meta', attrs: { name: 'twitter:image:alt', content: card.alt } },
    )
  }

  return { title, description, canonical, robots, tags }
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * `HeadData` as HTML, for the prerendered document.
 *
 * **Every tag carries `data-seo`, and it is not cosmetic.** `<Seo>` clears that
 * set before writing its own on hydration; without the marker here it cannot see
 * the tags this function produced, so it appends beside them — leaving two
 * `<link rel="canonical">` and two `hreflang` sets per page, and after a client
 * navigation the stale one comes first. Google reads the first canonical it
 * finds, which would be the previous page's. Caught in a browser rather than by
 * reading the code, and it is the reason `scripts/prerender.mjs` is worth
 * checking with one.
 */
export function renderHead(head: HeadData): string {
  const lines = [`<title>${escapeAttr(head.title)}</title>`]
  for (const { tag, attrs } of head.tags) {
    const rendered = Object.entries(attrs)
      .map(([k, v]) => `${k}="${escapeAttr(v)}"`)
      .join(' ')
    lines.push(`<${tag} ${rendered} data-seo>`)
  }
  return lines.join('\n    ')
}
